import { useNavigate } from 'react-router-dom';
import { useGlobalStore } from '../store/store.tsx';
import SideBarUser from './SideBarMenus/SideBarUser.tsx';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { UserRoleType } from '../types/userRoleType.ts';
import { ProfileService } from '../../features/profile/Services/ProfileService.ts';
import { UserRole } from '../types/userRole.ts';
import SideBarCustomerAdmin from './SideBarMenus/SideBarCustomerAdmin.tsx';
import SideBarSuperAdmin from './SideBarMenus/SideBarSuperAdmin.tsx';
import SideBarOfficer from './SideBarMenus/SideBarOfficer.tsx';
import { useOrganization } from '../../features/auth/hooks/useOrganization.tsx';
import { useOrganizationReset } from '../../common/hooks/useOrganizationReset.ts';

import {
    Avatar,
    Box,
    Dialog,
    DialogTitle,
    Drawer,
    List,
    ListItem,
    ListItemAvatar,
    ListItemButton,
    ListItemText,
    CircularProgress,
    Alert,
    Button,
    DialogActions,
    Toolbar,
    Typography,
} from '@mui/material';
import { ArrowCircleRight, SwapHoriz } from '@mui/icons-material';
import { UserOrganization } from '../../features/superAdmin/TenantOrganizations/OrganizationService.ts';

// Singleton instance to avoid recreating on every render
const profileService = new ProfileService();

interface SidebarProps {
    isOpen: boolean;
    toggleSidebar: () => void;
    isMobile: boolean;
}

const getMenuForRoles = (
    roles: UserRole[],
    handleClick: (route: string) => void
): React.ReactNode => {
    const hasAdminRole = roles.some(
        (role) =>
            role.roleName.toLowerCase().trim() ===
                UserRoleType.Admin.toLowerCase().trim() ||
            role.roleName.toLowerCase().trim() ===
                UserRoleType.Root.toLowerCase().trim()
    );

    const hasCustomerAdminRole = roles.some(
        (role) =>
            role.roleName.toLowerCase().trim() ===
                UserRoleType.LocalAdmin.toLowerCase().trim() ||
            role.roleName.toLowerCase().trim() ===
                UserRoleType.OperationalManager.toLowerCase().trim()
    );

    const hasOfficerRole = roles.some(
        (role) =>
            role.roleName.toLowerCase().trim() ===
                UserRoleType.MedicationsSafetyOfficer.toLowerCase().trim() ||
            role.roleName.toLowerCase().trim() ===
                UserRoleType.ControlledSubstancesOfficer.toLowerCase().trim()
    );

    if (hasAdminRole) {
        return <SideBarSuperAdmin handleMenuClick={handleClick} />;
    } else if (hasCustomerAdminRole) {
        return <SideBarCustomerAdmin handleMenuClick={handleClick} />;
    } else if (hasOfficerRole) {
        return <SideBarOfficer handleMenuClick={handleClick} />;
    } else {
        return <SideBarUser handleMenuClick={handleClick} />;
    }
};

const Sidebar = ({ isOpen, toggleSidebar, isMobile }: SidebarProps) => {
    const navigate = useNavigate();
    const { resetOverviews, profile, setProfile } = useGlobalStore();
    const { getMyOrganizations, switchOrganization } = useOrganization();
    const { resetOrganizationData } = useOrganizationReset();
    const [open, setOpen] = useState(false);
    const [userOrganizations, setUserOrganizations] = useState<
        UserOrganization[]
    >([]);
    const [hasMultipleOrganizations, setHasMultipleOrganizations] =
        useState<boolean>(false);
    const [currentOrganization, setCurrentOrganization] =
        useState<UserOrganization | null>(null);
    const [isSwitching, setIsSwitching] = useState(false);
    const [switchError, setSwitchError] = useState<string | null>(null);

    // Use refs to avoid stale closures
    const isOpenRef = useRef(isOpen);
    const toggleSidebarRef = useRef(toggleSidebar);

    useEffect(() => {
        isOpenRef.current = isOpen;
        toggleSidebarRef.current = toggleSidebar;
    }, [isOpen, toggleSidebar]);

    const handleClick = useCallback(
        (route: string) => {
            resetOverviews();
            navigate(route);
            if (isOpenRef.current) {
                toggleSidebarRef.current();
            }
        },
        [resetOverviews, navigate]
    );

    // Memoize the menu to avoid unnecessary re-renders
    const navMenu = useMemo(() => {
        if (profile?.userRoles) {
            return getMenuForRoles(profile.userRoles, handleClick);
        }
        return <SideBarUser handleMenuClick={handleClick} />;
    }, [profile?.userRoles, handleClick]);

    const handleOrganizationBannerClick = async () => {
        if (!profile) return;

        try {
            const organizations = await getMyOrganizations();

            if (organizations.length > 1) {
                // Show organization selection dialog
                setUserOrganizations(organizations);
                setOpen(true);
            }
            // If user only has one organization, do nothing (no point in switching)
        } catch (error) {
            console.error('Error fetching organizations:', error);
        }
    };

    const checkOrganization = useCallback(async () => {
        try {
            // Fetch user's organizations from the new API
            const organizations = await getMyOrganizations();
            setHasMultipleOrganizations(organizations.length > 1);

            // Find and set the current organization
            const current = organizations.find(
                (org) => org.isCurrentOrganization
            );
            if (current) {
                setCurrentOrganization(current);
            }
        } catch (error) {
            console.error('Error checking organizations:', error);
            setHasMultipleOrganizations(false);
            setCurrentOrganization(null);
        }
    }, [getMyOrganizations]);

    const handleClose = async (selectedOrg: UserOrganization | null) => {
        if (!selectedOrg) {
            setOpen(false);
            return;
        }

        // If the selected organization is already the current one, just close
        if (selectedOrg.isCurrentOrganization) {
            setOpen(false);
            return;
        }

        // Switch to the new organization (keep dialog open to show spinner)
        setIsSwitching(true);
        setSwitchError(null);

        try {
            // Switch organization and get new tokens
            await switchOrganization(selectedOrg.organizationId);

            // Reload profile with new organization
            const updatedProfile = await profileService.getProfile();
            setProfile(updatedProfile);

            // Update current organization state
            setCurrentOrganization(selectedOrg);

            // Close dialog
            setOpen(false);
            setIsSwitching(false);

            // Reset all organization-specific cached data
            resetOrganizationData();

            // Refresh current route to reload data
            navigate(0);
        } catch (error) {
            console.error('Error switching organization:', error);
            setSwitchError('Kunde inte byta organisation. Försök igen senare.');
            setIsSwitching(false);
        }
    };

    // Load profile and check organizations only on initial mount
    const hasInitialized = useRef(false);
    useEffect(() => {
        if (hasInitialized.current) return;
        hasInitialized.current = true;

        const initializeProfile = async () => {
            if (!profile) {
                const fetchedProfile = await profileService.getProfile();
                setProfile(fetchedProfile);
            }
            await checkOrganization();
        };
        initializeProfile();
    }, [profile, setProfile, checkOrganization]);

    return (
        <>
            <Drawer
                variant={isMobile ? 'temporary' : 'permanent'}
                open={isMobile ? isOpen : true}
                onClose={isMobile ? toggleSidebar : undefined}
                sx={{
                    width: 256,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: 256,
                        boxSizing: 'border-box',
                        bgcolor: 'background.paper',
                        borderRight: '1px solid',
                        borderColor: 'divider',
                    },
                }}
            >
                <Toolbar />
                <Box sx={{ flex: 1, overflowY: 'auto', py: 2, px: 1 }}>
                    {navMenu}
                </Box>

                {(profile?.selectedOrganization || currentOrganization) && (
                    <Box
                        sx={{
                            borderTop: '1px solid',
                            borderColor: 'divider',
                            px: 2,
                            py: 1,
                            bgcolor: 'background.default',
                            cursor: hasMultipleOrganizations
                                ? 'pointer'
                                : 'default',
                            '&:hover': hasMultipleOrganizations
                                ? { bgcolor: 'rgba(90, 173, 131, 0.04)' }
                                : {},
                        }}
                        onClick={
                            hasMultipleOrganizations
                                ? handleOrganizationBannerClick
                                : undefined
                        }
                        title={
                            hasMultipleOrganizations
                                ? 'Klicka för att byta organisation'
                                : undefined
                        }
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Box>
                                <Typography
                                    variant="body2"
                                    fontWeight={500}
                                    color="text.primary"
                                >
                                    {profile?.selectedOrganization?.name ||
                                        currentOrganization?.organizationName}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    {profile?.selectedOrganization
                                        ?.organizationType || 'Organisation'}
                                </Typography>
                            </Box>
                            {hasMultipleOrganizations && (
                                <SwapHoriz
                                    sx={{ color: 'text.secondary' }}
                                    fontSize="small"
                                />
                            )}
                        </Box>
                    </Box>
                )}
            </Drawer>

            <Dialog
                open={open}
                onClose={(_, reason) => {
                    if (!isSwitching && reason === 'backdropClick') {
                        handleClose(null);
                    }
                }}
                disableEscapeKeyDown
                PaperProps={{
                    style: {
                        width: 300,
                        boxShadow: '0 4px 20px 0 rgba(0,0,0,0.1)',
                        borderRadius: '12px',
                        transition: 'all 0.3s ease-in-out',
                    },
                }}
            >
                <DialogTitle>
                    {isSwitching
                        ? 'Byter organisation...'
                        : 'Välj Organisation'}
                </DialogTitle>
                {switchError && (
                    <Alert severity="error" sx={{ mx: 2, mb: 2 }}>
                        {switchError}
                    </Alert>
                )}
                {isSwitching ? (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            p: 4,
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : (
                    <List sx={{ pt: 0 }}>
                        {userOrganizations.map((org) => (
                            <ListItem
                                disablePadding
                                key={org.organizationId}
                                sx={{
                                    bgcolor: org.isCurrentOrganization
                                        ? 'rgba(90, 173, 131, 0.1)'
                                        : 'transparent',
                                    '&:hover': {
                                        backgroundColor:
                                            org.isCurrentOrganization
                                                ? 'rgba(90, 173, 131, 0.2)'
                                                : '#f5f5f5',
                                    },
                                }}
                            >
                                <ListItemButton
                                    onClick={() => handleClose(org)}
                                >
                                    <ListItemAvatar>
                                        <Avatar
                                            sx={{
                                                bgcolor:
                                                    org.isCurrentOrganization
                                                        ? 'primary.main'
                                                        : 'rgba(90, 173, 131, 0.2)',
                                                color: org.isCurrentOrganization
                                                    ? 'primary.contrastText'
                                                    : 'primary.main',
                                            }}
                                        >
                                            <ArrowCircleRight />
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={org.organizationName}
                                        secondary={
                                            org.isCurrentOrganization
                                                ? 'Aktiv organisation'
                                                : 'Klicka för att byta'
                                        }
                                    />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                )}
                <DialogActions>
                    <Button onClick={() => handleClose(null)} color="primary">
                        Avbryt
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default Sidebar;
